import Request from "../model/Request";
import Response from "../model/Response";
import {RestClient} from "../util/RestClient";
import {Config} from "../util/Config";
import SalePrice from "../entities/SalePrice";
import PagedResponse from "../entities/PagedResponse";
import Pagination from "../entities/Pagination";
import Currency from "../entities/Currency";
import MoneySalePriceValueRequest from "../model/salePrice/MoneySalePriceValueRequest";
import CustomCategoryWeightRangeSalePrice from "../model/packageDelivery/customCategory/CustomCategoryWeightRangeSalePrice";
import SalePriceItemType from "../entities/SalePriceItemType";

export module SalePriceService {
    export async function get(page: number = 1, pageSize: number = 15, filter?: string): Promise<Response<PagedResponse<SalePrice>>> {
        const url = Config.get("GET_SALE_PRICE_URL", {page, pageSize, filter});
        const request = new Request(url)
        const response = await RestClient.get(request)
        if (response.success) {
            const salePricesData = response.data?.data;
            const paginationData = response.data?.pagination;
            if (salePricesData && paginationData) {
                const data = salePricesData.map((t: any) => 
                    new SalePrice(
                        t.id,
                        t.name,
                        t.description,
                        t.articleSalePrices,
                        t.moneySalePrices,
                        t.topupSalePrices,
                        t.packageReferenceItemSalePrices,
                        t.packageCustomCategoryWeightRangeSalePrices,
                        t.miscellanySalePrices,
                        t.status
                    ));
                const pagination = new Pagination(paginationData.currentPage, paginationData.pageSize, paginationData.elementsCount, paginationData.pageCount);
                return new Response<PagedResponse<SalePrice>>(true, {data, pagination}, response.error)
            }
        }
        return new Response<PagedResponse<SalePrice>>(false, undefined, response.error, response.status);
    }

    export async function find(id: string): Promise<Response<SalePrice>> {
        const url = Config.get("FIND_SALE_PRICE_URL", {id});
        const request = new Request(url)
        const response = await RestClient.get(request);
        if (response.success) {
            const salePriceData = response.data;
            if (salePriceData) {
                const salePrice = new SalePrice(
                    salePriceData.id,
                    salePriceData.name,
                    salePriceData.description,
                    salePriceData.articleSalePrices,
                    salePriceData.moneySalePrices,
                    salePriceData.topupSalePrices,
                    salePriceData.referenceItemSalePrice,
                    salePriceData.packageCustomCategoryWeightRangeSalePrices,
                    salePriceData.miscellanySalePrices,
                    salePriceData.status
                );
                return new Response<SalePrice>(true, salePrice, response.error);
            }
        }
        return new Response<SalePrice>(true, undefined, response.error, response.status);
    }

    export async function getAgencySalePrice(): Promise<Response<SalePrice>> {
        const url = Config.get("GET_SALE_PRICE_FROM_AGENCY_URL");
        const request = new Request(url)
        const response = await RestClient.get(request);
        if (response.success) {
            const salePriceData = response.data;
            if (salePriceData) {
                const salePrice =
                    new SalePrice(
                        salePriceData.id,
                        salePriceData.name,
                        salePriceData.description,
                        salePriceData.articleSalePrices,
                        salePriceData.moneySalePrices,
                        salePriceData.topupSalePrices,
                        salePriceData.packageReferenceItemSalePrices,
                        salePriceData.packageCustomCategoryWeightRangeSalePrices,
                        salePriceData.miscellanySalePrices,
                        salePriceData.status
                    );
                return new Response<SalePrice>(true, salePrice, response.error);
            }
        }
        return new Response<SalePrice>(true, undefined, response.error, response.status);
    }

    export async function create(salePrice: SalePrice): Promise<Response<any>> {
        const url = Config.get("CREATE_SALE_PRICE_URL");
        const request = new Request(url, salePrice);
        return await RestClient.post(request);
    }

    export async function clone(id: string): Promise<Response<any>> {
        const url = Config.get("CLONE_SALE_PRICE_URL", {id});
        const request = new Request(url, {});
        return await RestClient.post(request);
    }

    export async function update(salePrice: SalePrice): Promise<Response<any>> {
        const url = Config.get("UPDATE_SALE_PRICE_URL", {id: salePrice.id});
        const request = new Request(url, salePrice);
        return await RestClient.put(request);
    }

    export async function setActive(salePrice: SalePrice): Promise<Response<any>> {
        const url = Config.get("SET_SALE_PRICE_ACTIVE_URL", {id: salePrice.id});
        const request = new Request(url);
        return await RestClient.get(request);
    }

    export async function setInactive(salePrice: SalePrice): Promise<Response<any>> {
        const url = Config.get("SET_SALE_PRICE_INACTIVE_URL", {id: salePrice.id});
        const request = new Request(url);
        return await RestClient.get(request);
    }

    export async function remove(salePriceId: string): Promise<Response<any>> {
        const url = Config.get("REMOVE_SALE_PRICE_URL", {id: salePriceId});
        const request = new Request(url);
        return await RestClient.remove(request);
    }

    export async function copyServicePrices(sourceSalePriceId: string, targetSalePriceIds: string[], type: SalePriceItemType): Promise<Response<any>> {
        const url = Config.get("COPY_SALE_PRICE_ITEMS_URL", {id: sourceSalePriceId});
        const request = new Request(url, {targetSalePriceIds, type});
        return await RestClient.put(request);
    }

    // ARTICLE SALE PRICE

    export async function createArticleSalePrice(salePriceId: string, articleId: string, salePrice: number, deliveryTimeDays: number | undefined, applyInAllSalePrices: boolean = false): Promise<Response<any>> {
        const url = Config.get("CREATE_ARTICLE_SALE_PRICE_URL", { id: salePriceId, articleId });
        const request = new Request(url, {salePrice, deliveryTimeDays, applyInAllSalePrices});
        return await RestClient.post(request);
    }

    export async function updateArticleSalePrice(salePriceId: string, articleId: string, articleSalePriceId: string, salePrice: number, deliveryTimeDays: number | undefined, applyInAllSalePrices: boolean = false): Promise<Response<any>> {
        const url = Config.get("UPDATE_ARTICLE_SALE_PRICE_URL", { id: salePriceId, articleId, articleSalePriceId });
        const request = new Request(url, {salePrice, deliveryTimeDays, applyInAllSalePrices});
        return await RestClient.put(request);
    }

    export async function removeArticleSalePrice(salePriceId: string, articleSalePriceId: string): Promise<Response<any>> {
        const url = Config.get("REMOVE_ARTICLE_SALE_PRICE_URL", { id: salePriceId, articleSalePriceId });
        const request = new Request(url);
        return await RestClient.remove(request);
    }

    // MONEY SALE PRICE

    export async function updateMoneySalePriceValue(salePriceId: string, moneySalePriceId: string, salePrice: number, isPercentage: boolean, currency: Currency): Promise<Response<any>> {
        const url = Config.get("UPDATE_MONEY_SALE_PRICE_URL", {id: salePriceId, moneySalePriceId});
        const request = new Request(url, {salePrice, isPercentage, currency});
        return await RestClient.put(request);
    }

    export async function createMoneySalePriceValues(
        salePriceId: string,
        fromValue: number,
        toValue: number,
        values: MoneySalePriceValueRequest[]
    ): Promise<Response<any>> {
        const url = Config.get("CREATE_MONEY_SALE_PRICE_CURRENCIES_URL", {id: salePriceId});
        const request = new Request(url, {
            fromValue, toValue,
            salePriceByCurrency: values
        });
        return await RestClient.post(request);
    }

    export async function updateMoneySalePriceValues(
        salePriceId: string,
        moneySalePriceId: string,
        fromValue: number,
        toValue: number,
        values: MoneySalePriceValueRequest[]
    ): Promise<Response<any>> {
        const url = Config.get("UPDATE_MONEY_SALE_PRICE_CURRENCIES_URL", {id: salePriceId, moneySalePriceId});
        const request = new Request(url, {
            fromValue, toValue,
            salePriceByCurrency: values
        });
        return await RestClient.put(request);
    }

    export async function deleteMoneySalePriceValues(
        salePriceId: string,
        moneySalePriceId: string,
    ): Promise<Response<any>> {
        const url = Config.get("REMOVE_MONEY_SALE_PRICE_URL", {id: salePriceId, moneySalePriceId});
        const request = new Request(url);
        return await RestClient.remove(request);
    }

    export async function updateTopupSalePrice(salePriceId: string, productId: string, salePrice: number): Promise<Response<any>> {
        const url = Config.get("UPDATE_TOPUP_SALE_PRICE_URL", {id: salePriceId, productId: productId});
        const request = new Request(url, {salePrice});
        return await RestClient.put(request);
    }

    export async function updateReferenceItemSalePrice(salePriceId: string, referenceItemId: string, salePrice: number): Promise<Response<any>> {
        const url = Config.get("UPDATE_REFERENCE_ITEM_SALE_PRICE_URL", { id: salePriceId, referenceItemId });
        const request = new Request(url, {salePrice});
        return await RestClient.put(request);
    }

    export async function updateMiscellanySalePrice(salePriceId: string, miscellanyCategoryId: string, salePrice: number): Promise<Response<any>> {
        const url = Config.get("UPDATE_MISCELLANY_SALE_PRICE_URL", { id: salePriceId, miscellanyCategoryId });
        const request = new Request(url, {salePrice});
        return await RestClient.put(request);
    }

     // Custom Category Weight Range Sale Prices
    export async function createCustomCategoryWeightRangeItemSalePrice(salePriceId: string, customCategoryId: string, weightRange: CustomCategoryWeightRangeSalePrice): Promise<Response<any>> {
        const url = Config.get("ADD_CUSTOM_CATEGORY_WEIGHT_RANGE_SALE_PRICE_URL", { id: salePriceId, customCategoryId });
        const request = new Request(url, { ...weightRange, price: weightRange.salePrice });
        return await RestClient.post(request);
    }

    export async function removeCustomCategoryWeightRangeItemSalePrice(salePriceId: string, salePriceCustomCategoryWeightRangeId: string): Promise<Response<any>> {
        const url = Config.get("REMOVE_CUSTOM_CATEGORY_WEIGHT_RANGE_SALE_PRICE_URL", { id: salePriceId, salePriceCustomCategoryWeightRangeId });
        const request = new Request(url);
        return await RestClient.remove(request);
    }

    export async function updateCustomCategoryWeightRangeItemSalePrice(salePriceId: string, salePriceCustomCategoryWeightRangeId: string, weightRange: CustomCategoryWeightRangeSalePrice): Promise<Response<any>> {
        const url = Config.get("UPDATE_CUSTOM_CATEGORY_WEIGHT_RANGE_SALE_PRICE_URL", { id: salePriceId, salePriceCustomCategoryWeightRangeId });
        const request = new Request(url, { ...weightRange, price: weightRange.salePrice });
        return await RestClient.put(request);
    }

}